'use client';
import { useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  CollectionsIcon,
  CommentsIcon,
  SettingsIcon,
  UploadIcon,
  WorksIcon,
} from '@/components/icons';
import { AuthorSocialBadges } from '@/features/profile/components/author-social-badges';
import { readProfileSocialLinks } from '@/features/profile/utils/social-link.util';
import { DonatModal } from './fanfic-page/donat-modal';
import { DonationButton } from './donation-button';

interface ProfileData {
  id: string | number;
  name: string;
  username?: string;
  email: string;
  bio?: string;
  instagramId?: string | null;
  vkId?: string | null;
  telegramId?: string | null;
  maxId?: string | null;
  avatar?: {
    url?: string;
    thumbnailURL?: string;
  };
  banner?: {
    url?: string;
    thumbnailURL?: string;
  };
  donation?: {
    donationUserId?: string | null;
    hasCard: boolean;
  };
  donationsEnabled?: boolean;
}

interface ProfileCardProps {
  userData: ProfileData | null;
  isLoadingUser: boolean;
  isOwnProfile?: boolean;
  onBannerChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isBannerLoading?: boolean;
  stats?: Array<{
    icon: React.ComponentType<{
      width: number;
      height: number;
      className?: string;
    }>;
    title: string;
    value: string;
  }>;
  profileActions?: React.ReactNode;
  children?: React.ReactNode;
}

export function ProfileCard({
  userData,
  isLoadingUser,
  isOwnProfile = false,
  onBannerChange,
  isBannerLoading = false,
  stats = [
    {
      icon: CollectionsIcon,
      title: 'Библиотека',
      value: '12 489',
    },
    {
      icon: WorksIcon,
      title: 'Работы',
      value: '13',
    },
    {
      icon: CommentsIcon,
      title: 'Комментарии',
      value: '127',
    },
  ],
  profileActions,
  children,
}: ProfileCardProps) {
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const [isDonatModalOpen, setIsDonatModalOpen] = useState(false);
  const socialLinks = readProfileSocialLinks(userData);
  const hasSocialLinks = Object.values(socialLinks).some(Boolean);

  const handleBannerClick = () => {
    bannerInputRef.current?.click();
  };

  return (
    <div className="bg-[#f2f3f7] min-h-screen">
      {/* Banner Section */}
      <div className="w-full px-4 mt-5 max-md:mt-7">
        <div className="max-w-[990px] xl:max-w-[1130px] mx-auto">
          <div className="relative h-64 md:h-80 bg-gradient-to-br from-blue-100 to-green-100 dark:from-blue-900/20 dark:to-green-900/20 rounded-2xl overflow-hidden">
            <div className="absolute inset-0">
              {userData?.banner?.url || userData?.banner?.thumbnailURL ? (
                <Image
                  src={userData.banner.url || userData.banner.thumbnailURL || ''}
                  alt="Profile banner"
                  fill
                  sizes="(max-width: 1024px) 100vw, (max-width: 1280px) 990px, 1130px"
                  quality={80}
                  priority
                  className="object-cover object-center"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-blue-200 via-green-200 to-blue-300 dark:from-blue-800/30 dark:via-green-800/30 dark:to-blue-700/30">
                  <div className="absolute inset-0 bg-[url('/images/hero/1.webp')] bg-cover bg-center opacity-20"></div>
                </div>
              )}
            </div>

            {/* Banner Edit Button - только для собственного профиля */}
            {isOwnProfile && (
              <div className="absolute top-4 right-4">
                <Button
                  size="sm"
                  onClick={handleBannerClick}
                  disabled={isBannerLoading}
                  className="bg-white/90 hover:bg-white text-black rounded-lg py-1.5 px-2 text-xs gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBannerLoading ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <UploadIcon width={16} height={16} className="w-4 h-4" />
                  )}
                  {isBannerLoading ? 'Загрузка...' : 'Изменить фон'}
                </Button>
              </div>
            )}

            {/* Hidden file input for banner upload */}
            {isOwnProfile && (
              <input
                ref={bannerInputRef}
                type="file"
                accept="image/*"
                onChange={onBannerChange}
                className="hidden"
              />
            )}
          </div>
        </div>
      </div>

      {/* Profile Info Section */}
      <div className="w-full px-4 -mt-16 relative z-10">
        <div className="max-w-[990px] xl:max-w-[1130px] mx-auto">
          <div className="relative mb-6 flex h-auto min-h-[72px] items-center rounded-2xl bg-white p-4 max-sm:mb-4 md:h-[92px] md:min-h-[92px] dark:bg-[#26282D]">
            {hasSocialLinks ? (
              <div className="absolute top-0 right-4 z-20 -translate-y-1/2">
                <AuthorSocialBadges links={socialLinks} />
              </div>
            ) : null}
            <div
              className={`flex w-full items-center gap-4 ${hasSocialLinks ? 'pr-[9.75rem] md:pr-[11rem]' : ''}`}
            >
              <div className="flex-shrink-0">
                {isLoadingUser ? (
                  <div className="w-10 h-10 md:w-10 md:h-10 rounded-3xl bg-muted dark:bg-[#3a3f47] flex items-center justify-center">
                    <div className="w-6 h-6 border-2 border-muted-foreground dark:border-white border-t-transparent rounded-3xl animate-spin"></div>
                  </div>
                ) : userData?.avatar?.url || userData?.avatar?.thumbnailURL ? (
                  <div className="w-16 h-16 md:w-[102px] max-md:w-20 max-md:h-20 absolute -top-12 max-md:-top-6 max-[520px]:-top-8 md:h-[102px] border-2 border-white dark:border-gray-800 shadow-lg rounded-3xl overflow-hidden">
                    <Image
                      src={userData.avatar.url || userData.avatar.thumbnailURL || ''}
                      alt={userData.name || 'User avatar'}
                      fill
                      sizes="(max-width: 768px) 76px, 100px"
                      quality={80}
                      priority
                      className="object-cover"
                    />
                  </div>
                ) : (
                  <span className="flex items-center justify-center w-16 h-16 md:w-[102px] max-md:w-20 max-md:h-20 absolute -top-12 max-md:-top-6 max-[520px]:-top-8 md:h-[102px] border-2 border-white dark:border-gray-800 shadow-lg rounded-3xl text-lg md:text-xl bg-muted dark:bg-[#3a3f47] text-muted-foreground dark:text-white">
                    {userData?.name?.[0] || userData?.email?.[0] || 'U'}
                  </span>
                )}
              </div>

              <div className="flex-1 min-w-0 ml-24 max-md:ml-20 flex flex-col md:flex-row md:items-center md:gap-4">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <h1 className="flex-1 min-w-0 text-lg md:text-2xl font-black text-foreground dark:text-white truncate">
                    {isLoadingUser ? 'Загрузка...' : userData?.name || 'Пользователь'}
                  </h1>
                  {/* Edit Profile Button - только для собственного профиля */}
                  {isOwnProfile && (
                    <div className="flex-shrink-0">
                      <Link href="/profile/edit">
                        <Button className="bg-[#F2F3F7] hover:bg-[#E8E9ED] text-black py-1.5 px-2 text-xs rounded-lg gap-2">
                          <SettingsIcon width={16} height={16} className="w-4 h-4" />
                          <span className="hidden md:inline">Редактировать</span>
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
                {/* <p className="text-sm md:text-base text-muted-foreground dark:text-[#898989] truncate">
                  @
                  {userData?.username ||
                    userData?.email?.split('@')[0] ||
                    'username'}
                </p> */}

                {/* Support Author Button - только для чужого профиля */}
                {!isOwnProfile && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 empty:hidden md:mt-0 md:flex-shrink-0 md:flex-nowrap">
                    {profileActions}
                    <DonationButton
                      variant="compact"
                      fullText
                      author={
                        userData
                          ? {
                              id:
                                typeof userData.id === 'string'
                                  ? parseInt(userData.id)
                                  : userData.id,
                              name: userData.name,
                              username: userData.username ?? '',
                              avatar: userData.avatar,
                              donation: {
                                donationUserId: userData?.donation?.donationUserId ?? '',
                                hasCard: userData?.donation?.hasCard ?? false,
                              },
                            }
                          : undefined
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-[990px] xl:max-w-[1130px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 max-md:gap-2">
            {stats.map((stat, index) => (
              <Card key={`${index}+${stat.title}`} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-[#F2F3F7] dark:bg-blue-900/30 rounded-lg">
                      <stat.icon width={24} height={24} className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground dark:text-[#898989]">
                        {stat.title}
                      </p>
                      <p className="text-2xl font-semibold text-foreground dark:text-white">
                        {stat.value}
                      </p>
                    </div>
                  </div>
                  {/* <ChevronRight className="w-5 h-5 text-muted-foreground dark:text-[#898989]" /> */}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Bio Section */}
        <div className="max-w-[990px] xl:max-w-[1130px] mx-auto">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-black text-foreground dark:text-white">ОБО МНЕ</h2>
              {/* Edit Bio Button - только для собственного профиля */}
              {/*{isOwnProfile && (*/}
              {/*  <Link href="/profile/edit">*/}
              {/*    <Button*/}
              {/*      size="sm"*/}
              {/*      className="rounded-lg py-1.5 px-2 text-xs gap-2 text-black bg-[#F2F3F7] hover:bg-[#E8E9ED]"*/}
              {/*    >*/}
              {/*      <EditIcon*/}
              {/*        width={16}*/}
              {/*        height={16}*/}
              {/*        className="w-4 h-4"*/}
              {/*      />*/}
              {/*      Изменить*/}
              {/*    </Button>*/}
              {/*  </Link>*/}
              {/*)}*/}
            </div>

            <div className="prose dark:prose-invert max-w-none">
              {userData?.bio ? (
                <p className="text-foreground dark:text-white break-words whitespace-pre-wrap">
                  {userData.bio}
                </p>
              ) : (
                <p className="text-muted-foreground dark:text-[#898989] italic">
                  Информация о себе не указана
                </p>
              )}
            </div>
          </Card>
          {children}
        </div>
      </div>

      {/* Donat Modal */}
      {!isOwnProfile && (
        <DonatModal
          isOpen={isDonatModalOpen}
          onClose={() => setIsDonatModalOpen(false)}
          author={
            userData
              ? {
                  id: typeof userData.id === 'string' ? parseInt(userData.id) : userData.id,
                  name: userData.name,
                  username: userData.username,
                  avatar: userData.avatar,
                  donation: {
                    donationUserId: userData?.donation?.donationUserId ?? '',
                    hasCard: userData?.donation?.hasCard ?? false,
                  },
                }
              : null
          }
        />
      )}
    </div>
  );
}
